import CardBody from "components/common/CardBody";
import CardHeader from "components/common/CardHeader";
import Column from "components/common/Column";
import CustomCard from "components/common/CustomCard";
import Row from "components/common/Row";
import React, { useEffect, useImperativeHandle, useMemo, useState } from "react";
import { getCompetencyAreas, getConcepts, getCountries, getLanguages } from "./ApiHelper";
import AutoCompleteDropdown from "components/autocomplete/AutoCompleteDropdown";
import { displayNotification, generateOptionsFromData } from "helpers";
import Button from "components/common/Button";
import { checkSimilarityIndex, getUmbrellaConcepts } from "../umbrella-concepts-cleaning/ApiHelper";

const ConceptsFilters = React.forwardRef(({ reloadData, isUmbrellaConcepts, title }, ref) => {

    const [countries, setCountries] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [competencyAreas, setCompetencyAreas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingConcepts, setLoadingConcepts] = useState(false);
    const [filters, setFilters] = useState({
        country_id: '',
        language_id: '',
        competency_area: '',
    });

    useImperativeHandle(ref, () => ({
        loadConcepts: handleLoad,
    }));

    const loadFilters = async () => {
        setLoading(true);
        const countriesRes = await getCountries();
        const languagesRes = await getLanguages();
        const competencyAreaRes = await getCompetencyAreas();
        if (countriesRes.success && languagesRes.success && competencyAreaRes.success) {
            setCountries(countriesRes.data);
            setLanguages(languagesRes.data);
            setCompetencyAreas(competencyAreaRes.data);
        }
        setLoading(false);
    }

    const handleLoad = async (similarityCheck = false, text = '') => {
        try {
            if (!(filters.competency_area || filters.language_id)) {
                displayNotification('error', "Language and Competency Area are required");
                return;
            }
            setLoadingConcepts(true);
            let data = [];
            if (similarityCheck) {
                data = await checkSimilarityIndex(filters.country_id, filters.language_id, filters.competency_area, text)
            } else {
                if (isUmbrellaConcepts) {
                    data = await getUmbrellaConcepts(filters.country_id, filters.language_id, filters.competency_area);
                } else {
                    data = await getConcepts(filters.country_id, filters.language_id, filters.competency_area);
                }
            }
            reloadData(data.data);
            setLoadingConcepts(false);
        } catch (e) {
            console.log(e);
            setLoadingConcepts(false);
        }
    }

    const countriesOptions = useMemo(() => {
        return generateOptionsFromData(countries, {
            value: "id",
            label: "country_name",
        });
    }, [countries]);
    const languagesOptions = useMemo(() => {
        return generateOptionsFromData(languages, {
            value: "id",
            label: "list_element_name",
        });
    }, [languages]);
    const competencyAreasOptions = useMemo(() => {
        return generateOptionsFromData(competencyAreas, {
            value: "id",
            label: "list_element_name",
        });
    }, [competencyAreas]);

    useEffect(() => {
        loadFilters();
    }, []);

    return (
        <>
            <CustomCard>
                <CardHeader>
                    <h1>{title}</h1>
                </CardHeader>
                <CardBody>
                    <h2>
                        Filters
                    </h2>
                    <Row>
                        <Column col={4}>
                            <AutoCompleteDropdown
                                className={loading ? "opacity-50" : "opacity-100"}
                                required={true}
                                error={true}
                                label={loading ? "Loading..." : "Countries"}
                                multiple={false}
                                hasselectall={false}
                                options={countriesOptions}
                                onChange={(event, value) =>
                                    setFilters({ ...filters, country_id: value?.value ?? '' })
                                }
                                value={countriesOptions.find(x => x.value === filters.country_id) ?? { value: '', label: '' }}
                            />
                          
                        </Column>
                        <Column col={4}>
                            <AutoCompleteDropdown
                                className={loading ? "opacity-50" : "opacity-100"}
                                required={true}
                                error={true}
                                label={loading ? "Loading..." : "Languages"}
                                multiple={false}
                                hasselectall={false}
                                options={languagesOptions}
                                onChange={(event, value) =>
                                    setFilters({ ...filters, language_id: value?.value ?? '' })
                                }
                                value={languagesOptions.find(x => x.value === filters.language_id) ?? { value: '', label: '' }}
                            />
                        </Column>
                        <Column col={4}>
                            <AutoCompleteDropdown
                                className={loading ? "opacity-50" : "opacity-100"}
                                required={true}
                                error={true}
                                label={loading ? "Loading..." : "Competency Areas"}
                                multiple={false}
                                hasselectall={false}
                                options={competencyAreasOptions}
                                onChange={(event, value) =>
                                    setFilters({ ...filters, competency_area: value?.value ?? '' })
                                }
                                value={competencyAreasOptions.find(x => x.value === filters.competency_area) ?? { value: '', label: '' }}
                            />
                        </Column>
                        <Column className="text-end">
                            <Button
                                className="me-4"
                                onClick={handleLoad}
                                disabled={loadingConcepts}
                            >
                                {
                                    loadingConcepts ? "Loading..." : "Load"
                                }
                            </Button>
                        </Column>
                    </Row>
                </CardBody>
            </CustomCard>
        </>
    );
});

ConceptsFilters.defaultProps = {
    title: "Qualitative Cleaning - Concepts",
    isUmbrellaConcepts: false,
    reloadData: () => { },
}

export default ConceptsFilters;